import { useEffect, useRef, useState } from 'react'
import { useForm, Controller, useWatch } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { CheckBox } from 'components/atoms/check-box'
import { Button } from 'components/atoms/button'
import { Label } from 'components/atoms/Label'
import { TextArea } from 'components/atoms/TextArea'
import { TinyMceEditor } from 'components/molecules/tiny-mce-editor'
import { SmsSegmentCounter } from 'components/molecules/sms-segment-counter'
import { useLocalStorage } from 'hooks/useLocalStorage'
import { useStore } from 'hooks/useStore'
import { stripHtmlToText } from 'utils/html-text'

interface SendContactMessageModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	contactCount: number
	isSelectAll: boolean
	onSend: (message: string, isViaEmail: boolean, isViaSms: boolean, smsMessage: string | null) => void
	isSending: boolean
	/** Human-readable description of who will receive the message */
	selectionDescription?: string
}

const validationSchema = Yup.object().shape({
	message: Yup.string()
		.required('Please enter a message')
		.test('message-has-text', 'Please enter a message', value =>
			stripHtmlToText(value ?? '').trim().length > 0
		),
	smsMessage: Yup.string().nullable().notRequired(),
	sendViaEmail: Yup.boolean().required(),
	sendViaSms: Yup.boolean().required(),
})

export function SendContactMessageModal({
	open,
	setOpen,
	contactCount,
	isSelectAll,
	onSend,
	isSending,
	selectionDescription,
}: SendContactMessageModalProps) {
	const { user } = useStore()
	const storageKey = `sendContactMessage_${user?.userId ?? 'anon'}`
	const smsStorageKey = `sendContactMessageSms_${user?.userId ?? 'anon'}`
	const [storedMessage, setStoredMessage] = useLocalStorage(storageKey, '')
	const [storedSmsMessage, setStoredSmsMessage] = useLocalStorage(smsStorageKey, '')
	const [isUploadingImage, setIsUploadingImage] = useState(false)

	const {
		control,
		handleSubmit,
		setValue,
		reset,
		formState: { errors },
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
		defaultValues: {
			message: storedMessage || '',
			smsMessage: storedSmsMessage || null,
			sendViaEmail: true,
			sendViaSms: false,
		},
	})

	const currentMessage = useWatch({ control, name: 'message' }) ?? ''
	const currentSmsMessage = useWatch({ control, name: 'smsMessage' })
	const sendViaSms = useWatch({ control, name: 'sendViaSms' }) ?? false

	// Persist draft to localStorage
	useEffect(() => {
		setStoredMessage(currentMessage || '')
	}, [currentMessage, setStoredMessage])
	useEffect(() => {
		setStoredSmsMessage(currentSmsMessage || '')
	}, [currentSmsMessage, setStoredSmsMessage])

	// Restore draft when modal opens (ref-based so it only fires on open change)
	const storedMessageRef = useRef(storedMessage)
	storedMessageRef.current = storedMessage
	const storedSmsMessageRef = useRef(storedSmsMessage)
	storedSmsMessageRef.current = storedSmsMessage
	useEffect(() => {
		if (open) {
			setValue('message', storedMessageRef.current || '')
			setValue('smsMessage', storedSmsMessageRef.current || null)
		}
	}, [open, setValue])

	const onSubmit = (data: any) => {
		onSend(data.message, true, data.sendViaSms ?? false, data.smsMessage?.trim() || null)
		setStoredMessage('')
		setStoredSmsMessage('')
		reset({ message: '', smsMessage: null, sendViaEmail: true, sendViaSms: false })
	}

	const hasText = stripHtmlToText(currentMessage).trim().length > 0

	return (
		<BaseModal
			open={open}
			setOpen={isSending ? undefined : setOpen}
			title="Send Message"
			customSize="w-screen sm:w-[65vw] sm:max-w-[760px]"
			bodyClassName="sm:h-fit sm:max-h-[92vh]"
			preventClose={isSending}
		>
			<form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 text-left">
				{/* Recipient count */}
				<div className="flex items-center gap-2 px-3 py-2.5 bg-white/[0.04] border border-white/[0.08] rounded-lg">
					<FontAwesomeIcon
						icon={contactCount === 1 ? ['fas', 'user'] : ['fas', 'users']}
						className="text-secondary-text/60 text-sm"
					/>
					<span className="text-sm text-secondary-text">
						Sending to{' '}
						<span className="text-primary-text font-semibold">
							{selectionDescription ||
								(isSelectAll
									? `all ${contactCount.toLocaleString()} contacts`
									: `${contactCount.toLocaleString()} ${contactCount === 1 ? 'contact' : 'contacts'}`)}
						</span>
					</span>
				</div>

				{/* Message */}
				<div className="flex flex-col gap-1.5">
					<label className="text-xs font-medium text-secondary-text">Message</label>
					<Controller
						control={control}
						name="message"
						render={({ field }) => (
							<TinyMceEditor
								variant="compact"
								value={field.value || ''}
								onChange={field.onChange}
								error={!!errors.message}
								disabled={isSending}
								className="w-full"
								onUploadingChange={setIsUploadingImage}
							/>
						)}
					/>
					{errors.message && (
						<p className="text-xs text-secondary-red">{errors.message.message as string}</p>
					)}
				</div>

				{sendViaSms && (
					<div className="flex flex-col gap-1.5">
						<Label label="SMS text (optional)" className="block text-left" />
						<Controller
							control={control}
							name="smsMessage"
							render={({ field }) => (
								<TextArea
									minRows={3}
									placeholder="Leave blank to send a plain-text version of your message above"
									value={field.value || ''}
									onChange={field.onChange}
									hasError={!!errors.smsMessage}
									disabled={isSending}
								/>
							)}
						/>
						<SmsSegmentCounter
							{...(currentSmsMessage?.trim() ? { text: currentSmsMessage } : { html: currentMessage || '' })}
							className="text-left"
						/>
						<p className="text-secondary-text text-xs text-left">
							{currentSmsMessage?.trim()
								? 'Sending your custom SMS text.'
								: 'SMS will be generated from your message above.'}
						</p>
						{errors.smsMessage && (
							<p className="text-xs text-secondary-red">{errors.smsMessage.message as string}</p>
						)}
					</div>
				)}

				{/* Delivery methods */}
				<div className="flex flex-col gap-2">
					<label className="text-xs font-medium text-secondary-text">Delivery Method</label>
					<div className="flex items-center gap-1.5">
						<CheckBox checked={true} disabled />
						<span className="text-sm text-secondary-text">Send via email</span>
					</div>
					{/* ENG-2644: hide the SMS option when this organizer is blocked from sending SMS */}
					{!user?.disableSendSms && (
						<Controller
							name="sendViaSms"
							control={control}
							render={({ field }) => (
								<div className="flex items-center gap-1.5">
									<CheckBox
										checked={field.value}
										onChange={e => field.onChange((e.target as HTMLInputElement).checked)}
									/>
									<span className="text-sm text-primary-text">Send via text message</span>
								</div>
							)}
						/>
					)}
				</div>

				{/* Actions */}
				<div className="border-t border-white/[0.06] pt-4 flex gap-3">
					<Button
						variant="secondary"
						fullWidth
						type="button"
						onClick={() => setOpen(false)}
						disabled={isSending}
					>
						Cancel
					</Button>
					<Button
						variant="primary"
						fullWidth
						type="submit"
						disabled={isSending || !hasText || isUploadingImage}
					>
						{isSending ? (
							<>
								<FontAwesomeIcon icon={['fas', 'spinner']} spin className="mr-2" />
								Sending...
							</>
						) : (
							<>
								<FontAwesomeIcon icon={['fas', 'paper-plane']} className="mr-2" />
								Send
							</>
						)}
					</Button>
				</div>
			</form>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface SendContactMessageModalManagedProps {
	contactCount: number
	isSelectAll: boolean
	onSend: (message: string, isViaEmail: boolean, isViaSms: boolean, smsMessage: string | null) => void
	isSending: boolean
	selectionDescription?: string
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'send-contact-message': SendContactMessageModalManagedProps
	}
}

export function ManagedSendContactMessageModal({
	onClose,
	...rest
}: SendContactMessageModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<SendContactMessageModal
			open={open}
			setOpen={(v: boolean) => {
				if (!v) handleClose()
			}}
			{...rest}
		/>
	)
}
